export type WordEnergy = {
    rms: number;
    ratio: number;
};

export type TranscriptWord = {
    start: number;
    end: number;
    word: string;
    rawWord: string;
    speaker?: string;
    speakerId?: number;
    type?: string;
    energy?: WordEnergy;
};

// Minimum energy ratio threshold - words below this are considered too faint
const MIN_ENERGY_RATIO = 0.2;

// Defensive timing sanitization: some providers occasionally emit missing/incorrect end timestamps
// (e.g. a word spanning tens of seconds). Clamp so a single bad token can't cover large ranges.
const DEFAULT_WORD_DURATION_SEC = 0.35;
const MAX_INFERRED_WORD_DURATION_SEC = 1.2;
const MAX_WORD_DURATION_SEC = 2.5;

/**
 * Parse transcript JSON of the shape:
 * {
 *   words: Array<{ text: string; t0: number; t1: number; speakerId?: string; energy?: WordEnergy }>
 * }
 * Also supports legacy format with start/end and speaker_id fields.
 * - Filters for type === 'word' (if type field present)
 * - Filters out faint words (energy.ratio < 0.2) if energy data is present
 * - Normalizes fields and sorts by start time
 */
export function ParseTranscriptWords(data: any): TranscriptWord[] {
    if (!data || !Array.isArray(data.words)) return [];
    const out: TranscriptWord[] = [];
    for (const w of data.words) {
        if (!w) continue;
        const kind = w.type ?? 'word';
        if (kind !== 'word') continue;

        // Filter out faint words if energy data is present
        // Keep words with no energy data or ratio >= threshold
        if (w.energy?.ratio != null && w.energy.ratio > 0 && w.energy.ratio < MIN_ENERGY_RATIO) {
            continue;
        }

        // Support both t0/t1 (new) and start/end (legacy) field names
        const start = Number(w.t0 ?? w.start ?? 0);
        const end = Number(w.t1 ?? w.end ?? start);
        const text = String(w.text ?? '').trim();
        if (!text) continue;
        if (!Number.isFinite(start)) continue;
        // Support both speakerId (new) and speaker_id (legacy) field names
        const speakerIdRaw = w.speakerId ?? w.speaker_id;
        const speakerIdParsed = speakerIdRaw != null ? Number(speakerIdRaw) : 0;
        const speakerId = Number.isFinite(speakerIdParsed) ? speakerIdParsed : 0;
        out.push({
            start: Math.max(0, start),
            end: Number.isFinite(end) ? Math.max(0, end) : Math.max(0, start),
            word: text,
            rawWord: text,
            speakerId,
            speaker: `Speaker ${speakerId}`,
            type: kind,
            energy: w.energy,
        });
    }
    out.sort((a, b) => a.start - b.start);

    // Second pass: infer/clamp end timestamps using the next word start as a hint.
    for (let i = 0; i < out.length; i++) {
        const cur = out[i];
        const nextStartRaw = out[i + 1]?.start;
        const nextStart = Number.isFinite(nextStartRaw) && nextStartRaw > cur.start ? Number(nextStartRaw) : null;

        let start = cur.start;
        let end = Number.isFinite(cur.end) ? cur.end : start;

        // Normalize inverted/empty ranges.
        if (end <= start) {
            if (nextStart != null) {
                end = Math.min(nextStart, start + MAX_INFERRED_WORD_DURATION_SEC);
            } else {
                end = start + DEFAULT_WORD_DURATION_SEC;
            }
        }

        // Clamp implausibly-long words.
        const dur = end - start;
        if (!Number.isFinite(dur) || dur > MAX_WORD_DURATION_SEC) {
            const cap = start + MAX_WORD_DURATION_SEC;
            end = nextStart != null ? Math.min(cap, nextStart) : cap;
        }

        if (end < start) end = start;
        cur.start = start;
        cur.end = end;
    }

    return out;
}
